import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import mapboxgl from "mapbox-gl";
import { setIsMidstreamNavigatorFilter } from "store/features";
import {
  addManyPropertiesToFilter as addManyFacilityPropertiesToFilter,
  filterWellsFromList as filterFacilitiesFromList,
  setFilterToSystemDefault as setFacilityFilterToSystemDefault,
  setWellListExtent as setFacilityWellListExtent,
  setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter
} from "store/features/filter/facilityFilterSlice";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import { selectSelectedFacilities, selectSelectedWells } from "stores/map/mapSelectors";

import { useUserEvents } from "hooks";

import useGetSalesFacilityFromWell from "components/map/hooks/useGetSalesFacilityFromWell";
import {
  midstreamNavigatorActions,
  useMidstreamNavigatorContext
} from "components/midstream/midstream-navigator/context/MidstreamNavigatorContext";

import useMutateFacilitiesEnvelope from "../../../hooks/useMutateFacilitiesEnvelope";
import { FilterKey } from "../FilterButton";
import { useNetworkBoundsManager } from "./useNetworkBoundsManager";

/**
 * Hook for filtering to sales facilities for a selected well.
 * Now uses map selection state instead of WellListProvider context.
 */
export const useFilterToSalesFacilities = (mapbox: mapboxgl.Map) => {
  const dispatch = useDispatch();
  const { panToFocusAreaBounds } = useNetworkBoundsManager(mapbox);
  const [midstreamState, midstreamDispatch] = useMidstreamNavigatorContext();
  const { sendSalesFacilitiesFilterEvent, sendMidstreamNavigatorButtonToggledEvent } =
    useUserEvents();

  // Get selected entities from Zustand map store
  const selectedWells = useMapStore(selectSelectedWells);
  const selectedFacilities = useMapStore(selectSelectedFacilities);

  // Zustand map store: actions
  const setSalesEnvelope = useMapStore((s) => s.setSalesEnvelope);

  // Get current property filters to preserve them when clearing well list
  const currentPropertyFilters = useSelector(
    (state: RootState) => state.facilityFilter.propertiesFilter
  );

  const { mutateAsync: getFacilitiesEnvelope } = useMutateFacilitiesEnvelope();

  const prevSelectedWellId = useRef<Maybe<string>>(null);
  const hasProcessedCurrentWell = useRef<boolean>(false);

  const getSelectedWellId = (): Maybe<string> => {
    const wellUwis = Object.keys(selectedWells || {});
    if (wellUwis.length > 0) {
      return wellUwis[0];
    }

    const facilityUwis = Object.keys(selectedFacilities || {});
    return facilityUwis.length > 0 ? facilityUwis[0] : null;
  };

  const selectedWellId = getSelectedWellId();

  const { data: facilityData, error } = useGetSalesFacilityFromWell(selectedWellId);

  const filterToSalesFacilities = useCallback(
    async (hasWellChanged: boolean = false) => {
      if (!selectedWellId) {
        toast.warn("Please select a well on the map first.");
        return;
      }
      if (error) {
        toast.error("Could not get Sales facilities for selected well");
        return;
      }

      if (!facilityData || facilityData.length === 0) {
        midstreamDispatch(
          midstreamNavigatorActions.removeFilter(FilterKey.SalesFacilities)
        );
        setSalesEnvelope(null);
        toast.warn("No sales facilities found for selected well.");
        return false;
      }
      const facilityIds = facilityData.map((item) => item.facilityId);
      const envelope = await getFacilitiesEnvelope({
        wellId: selectedWellId,
        facilityIds: facilityIds
      });

      setSalesEnvelope(envelope);
      // Filter facilities to show sales facilities
      // Set midstream navigator flags to prevent automatic zooming
      dispatch(setIsMidstreamNavigatorFilter(true));
      dispatch(setIsMidstreamNavigatorFacilityFilter(true));

      dispatch(
        filterFacilitiesFromList({
          wellList: facilityIds,
          resetMidstreamFlag: false
        })
      );

      const logInfo = {
        selectedWell: selectedWellId,
        facilityFilterCount: [...new Set(facilityIds)].length
      };
      if (hasWellChanged) {
        sendSalesFacilitiesFilterEvent({
          source: "Selected Well Changed",
          ...logInfo
        });
      } else {
        sendMidstreamNavigatorButtonToggledEvent({
          filterKey: FilterKey.SalesFacilities,
          ...logInfo
        });
      }
      await panToFocusAreaBounds({
        singlePadding: 50,
        combinedPadding: 75,
        duration: 3,
        salesEnvelope: envelope
      });
    },
    [
      selectedWellId,
      facilityData,
      error,
      setSalesEnvelope,
      dispatch,
      panToFocusAreaBounds,
      getFacilitiesEnvelope,
      midstreamDispatch
    ]
  );

  const removeFilterToSalesFacilities = useCallback(
    async (isToggled: boolean = false) => {
      // Simply clear the sales envelope and extent - don't touch the filters
      // The external monitoring system will handle removing the actual filter
      if (isToggled) dispatch(setFacilityFilterToSystemDefault());
      setSalesEnvelope(null);
      dispatch(setFacilityWellListExtent(undefined));
    },
    [dispatch, setSalesEnvelope]
  );
  useEffect(() => {
    const currentSelectedWellId = getSelectedWellId();
    if (!prevSelectedWellId.current) prevSelectedWellId.current = currentSelectedWellId;
    const hasWellChanged = prevSelectedWellId.current !== currentSelectedWellId;
    const isSalesFacilitiesActive = midstreamState?.activeFilters?.includes(
      FilterKey.SalesFacilities
    );

    // Reset processed flag when wells change
    if (hasWellChanged) {
      hasProcessedCurrentWell.current = false;
    }

    // Only auto-trigger if sales facilities filter is currently active and we haven't processed this selection yet
    const shouldProcess =
      currentSelectedWellId &&
      isSalesFacilitiesActive &&
      !hasProcessedCurrentWell.current &&
      facilityData !== undefined;

    if (shouldProcess) {
      hasProcessedCurrentWell.current = true;

      // Update ref with current selection
      if (hasWellChanged) {
        prevSelectedWellId.current = selectedWellId;
      }
      dispatch(setIsMidstreamNavigatorFilter(true));
      dispatch(setIsMidstreamNavigatorFacilityFilter(true));
      filterToSalesFacilities(hasWellChanged);
    }
  }, [
    selectedWellId,
    facilityData,
    midstreamState.activeFilters,
    filterToSalesFacilities,
    dispatch,
    currentPropertyFilters,
    addManyFacilityPropertiesToFilter
  ]);
  return {
    filterToSalesFacilities,
    removeFilterToSalesFacilities,
    facilityData,
    selectedWellId
  };
};
